import {AssetOperationTimeSource, ELECTRICITY_QUERY_KEY, SCHEDULE_QUERY_KEY} from '@domain/schedule'
import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'

const COST_AVOIDANCE_FOR_RANGE_QUERY_KEY = 'costAvoidanceForRange'
const STOCK_DEVELOPMENT_QUERY_KEY = 'stockDevelopment'

interface RemoveAutofillItemsError {
  message?: string
}

interface RemoveAutofillItemsPayload {
  plantCode: string
  start: string
  end: string
}

const removeAutofillItems = async ({
  plantCode,
  start,
  end
}: RemoveAutofillItemsPayload): Promise<void> => {
  await ApiClient.axiosInstance.delete(`/plants/${plantCode}/schedules/current/items`, {
    params: {start, end, source: AssetOperationTimeSource.Optimizer}
  })
}

export const useRemoveAutofillItems = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError<RemoveAutofillItemsError>, RemoveAutofillItemsPayload>({
    mutationFn: removeAutofillItems,
    onError: (error) => {
      const message = error.response?.data.message
      if (typeof message === 'string') spawnErrorToast(message)
      else if (message) raiseError(new Error(JSON.stringify(message)))
    },
    onSuccess: () => {
      spawnSuccessToast(t('success.removeAutofillItems'))
    },
    onSettled: (data, error, {plantCode}) => {
      void queryClient.invalidateQueries({queryKey: [SCHEDULE_QUERY_KEY], refetchType: 'all'})
      void queryClient.invalidateQueries({
        queryKey: [ELECTRICITY_QUERY_KEY, {plantCode}],
        refetchType: 'all'
      })
      void queryClient.invalidateQueries({
        queryKey: [COST_AVOIDANCE_FOR_RANGE_QUERY_KEY],
        refetchType: 'all'
      })
      void queryClient.invalidateQueries({
        queryKey: [STOCK_DEVELOPMENT_QUERY_KEY],
        refetchType: 'all'
      })
    }
  })
}
